import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircleOff, Pencil, Plus, X } from 'lucide-react';
import { api, type AdminChatBan, type AuthUser, type Player } from '@/lib/api';
import { cn } from '@/lib/utils';
import { RosterTabs } from './Roster';

const DURATION_PRESETS = [12, 24, 48, 96, 168];
const MIN_STARTS_AT_ISO = '2026-08-01T00:00:00.000Z';

function pad(n: number) {
  return n < 10 ? `0${n}` : String(n);
}

function playerLabel(p: Pick<Player, 'name' | 'displayName'>): string {
  return p.displayName?.trim() || p.name;
}

function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  const tzOffset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - tzOffset * 60_000);
  return local.toISOString().slice(0, 16);
}

function localToIso(local: string): string {
  return new Date(local).toISOString();
}

function nowLocalDatetime(): string {
  const d = new Date();
  const tzOffset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - tzOffset * 60_000);
  return local.toISOString().slice(0, 16);
}

function minStartsAtLocal(): string {
  const d = new Date(MIN_STARTS_AT_ISO);
  const tzOffset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - tzOffset * 60_000);
  return local.toISOString().slice(0, 16);
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return 'ended';
  const total = Math.floor(ms / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (d > 0) return `${d}d ${pad(h)}:${pad(m)}:${pad(s)}`;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export default function AdminChatBans(_props: { user: AuthUser }) {
  const q = useQuery({
    queryKey: ['admin-chat-bans'],
    queryFn: api.listAdminChatBans,
    refetchInterval: 30_000,
  });
  const players = useQuery({
    queryKey: ['players'],
    queryFn: () => api.listPlayers(),
  });

  const [dialog, setDialog] = useState<
    | { mode: 'create' }
    | { mode: 'edit'; ban: AdminChatBan }
    | null
  >(null);

  const bans = q.data?.bans ?? [];
  const active = bans.filter((b) => b.active);
  const past = bans.filter((b) => !b.active);

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 p-4">
      <RosterTabs />
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Chat bans</h1>
          <p className="text-sm text-slate-400">
            Manage active bans and history. Players report their own via
            magic-link, but you can also create and correct them here.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setDialog({ mode: 'create' })}
          className="inline-flex items-center gap-1.5 rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium hover:bg-indigo-500"
        >
          <Plus size={14} /> New ban
        </button>
      </header>

      <section className="space-y-2">
        <h2 className="text-xs font-medium uppercase tracking-wider text-slate-400">
          Active ({active.length})
        </h2>
        {q.isLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : active.length === 0 ? (
          <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-6 text-center text-sm text-slate-500">
            No active chat bans.
          </div>
        ) : (
          <div className="divide-y divide-slate-800 rounded-lg border border-slate-800 bg-slate-900/40">
            {active.map((b) => (
              <ChatBanRow
                key={b.id}
                ban={b}
                onEdit={() => setDialog({ mode: 'edit', ban: b })}
              />
            ))}
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-xs font-medium uppercase tracking-wider text-slate-400">
          Past ({past.length})
        </h2>
        {past.length === 0 ? (
          <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-6 text-center text-sm text-slate-500">
            Nothing yet.
          </div>
        ) : (
          <div className="divide-y divide-slate-800 rounded-lg border border-slate-800 bg-slate-900/40">
            {past.slice(0, 40).map((b) => (
              <PastRow key={b.id} ban={b} />
            ))}
          </div>
        )}
      </section>

      {dialog && (
        <ChatBanDialog
          mode={dialog.mode}
          existing={dialog.mode === 'edit' ? dialog.ban : null}
          players={players.data ?? []}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}

function ChatBanRow({
  ban,
  onEdit,
}: {
  ban: AdminChatBan;
  onEdit: () => void;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const qc = useQueryClient();
  const end = useMutation({
    mutationFn: (id: number) => api.endAdminChatBan(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-chat-bans'] }),
  });
  const delta = Date.parse(ban.endsAtUtc) - now;
  return (
    <div className="flex flex-wrap items-center gap-3 px-3 py-2.5 text-sm">
      <MessageCircleOff size={14} className="text-rose-300" />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-slate-100">
          {playerLabel(ban.player)}{' '}
          <span className="text-xs text-slate-500">· {ban.player.rank}</span>
        </div>
        <div className="text-[11px] text-slate-500 tabular-nums">
          {ban.startsAtUtc.slice(0, 16).replace('T', ' ')} UTC ·{' '}
          {ban.durationHours}h
        </div>
        {ban.notes && (
          <div className="mt-0.5 text-[11px] italic text-slate-400">
            "{ban.notes}"
          </div>
        )}
      </div>
      <div className="whitespace-nowrap text-xs font-semibold tabular-nums text-rose-200">
        {formatCountdown(delta)}
      </div>
      <div className="flex gap-1">
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-200 hover:border-slate-500"
        >
          <Pencil size={11} /> Edit
        </button>
        <button
          type="button"
          onClick={() => {
            if (confirm(`End chat ban for ${playerLabel(ban.player)}?`)) {
              end.mutate(ban.id);
            }
          }}
          disabled={end.isPending}
          className="inline-flex items-center gap-1 rounded border border-rose-700/60 bg-rose-950/60 px-2 py-1 text-xs text-rose-100 hover:border-rose-500 disabled:opacity-50"
        >
          <X size={11} /> End
        </button>
      </div>
    </div>
  );
}

function PastRow({ ban }: { ban: AdminChatBan }) {
  const endedLabel = ban.endedAt
    ? `ended ${ban.endedAt.slice(0, 16).replace('T', ' ')}`
    : `expired ${ban.endsAtUtc.slice(0, 16).replace('T', ' ')}`;
  return (
    <div className="flex items-center gap-3 px-3 py-2 text-sm">
      <div className="min-w-0 flex-1">
        <div className="truncate text-slate-200">
          {playerLabel(ban.player)}
        </div>
        <div className="text-[11px] text-slate-500 tabular-nums">
          {ban.startsAtUtc.slice(0, 16).replace('T', ' ')} · {ban.durationHours}h
          · {endedLabel}
        </div>
      </div>
      <div className="whitespace-nowrap text-[11px] tabular-nums text-slate-500">
        effective {ban.effectiveHours}h
      </div>
    </div>
  );
}

function ChatBanDialog({
  mode,
  existing,
  players,
  onClose,
}: {
  mode: 'create' | 'edit';
  existing: AdminChatBan | null;
  players: Player[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [playerId, setPlayerId] = useState<number | null>(
    existing?.player.id ?? null,
  );
  const [playerSearch, setPlayerSearch] = useState('');
  const [startsLocal, setStartsLocal] = useState<string>(() => {
    const initial = existing
      ? isoToLocalInput(existing.startsAtUtc)
      : nowLocalDatetime();
    const min = minStartsAtLocal();
    return initial < min ? min : initial;
  });
  const [durationHours, setDurationHours] = useState<number>(
    existing?.durationHours ?? 24,
  );
  const [notes, setNotes] = useState<string>(existing?.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const activePlayers = useMemo(
    () => players.filter((p) => p.active),
    [players],
  );
  const filtered = useMemo(() => {
    const q = playerSearch.trim().toLowerCase();
    if (!q) return activePlayers.slice(0, 30);
    return activePlayers
      .filter((p) => playerLabel(p).toLowerCase().includes(q))
      .slice(0, 30);
  }, [activePlayers, playerSearch]);

  const create = useMutation({
    mutationFn: () =>
      api.createAdminChatBan({
        playerId: playerId!,
        startsAtUtc: localToIso(startsLocal),
        durationHours,
        notes: notes.trim() ? notes.trim() : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-chat-bans'] });
      onClose();
    },
    onError: (e: unknown) => setError((e as Error).message),
  });
  const update = useMutation({
    mutationFn: () =>
      api.updateAdminChatBan(existing!.id, {
        startsAtUtc: localToIso(startsLocal),
        durationHours,
        notes: notes.trim() ? notes.trim() : null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-chat-bans'] });
      onClose();
    },
    onError: (e: unknown) => setError((e as Error).message),
  });

  const busy = create.isPending || update.isPending;
  const canSubmit = playerId != null && startsLocal.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-lg border border-slate-800 bg-slate-950 p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">
            {mode === 'create' ? 'New chat ban' : 'Edit chat ban'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:text-slate-100"
          >
            <X size={16} />
          </button>
        </div>

        {mode === 'create' ? (
          <div className="mb-3">
            <div className="mb-1 text-xs text-slate-400">Player</div>
            <input
              type="text"
              value={playerSearch}
              onChange={(e) => setPlayerSearch(e.target.value)}
              placeholder="Search…"
              className="mb-1 w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100"
            />
            <div className="max-h-40 overflow-y-auto rounded border border-slate-800 bg-slate-900/40">
              {filtered.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPlayerId(p.id)}
                  className={cn(
                    'block w-full px-2 py-1 text-left text-sm',
                    playerId === p.id
                      ? 'bg-indigo-600/40 text-indigo-100'
                      : 'text-slate-200 hover:bg-slate-800',
                  )}
                >
                  {playerLabel(p)}{' '}
                  <span className="text-xs text-slate-500">· {p.rank}</span>
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="px-2 py-2 text-xs text-slate-500">
                  No matches.
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="mb-3 rounded border border-slate-800 bg-slate-900/40 px-2 py-1.5 text-sm">
            <span className="text-slate-400">Player:</span>{' '}
            <span className="font-medium text-slate-100">
              {existing && playerLabel(existing.player)}
            </span>
          </div>
        )}

        <label className="mb-3 block text-xs">
          <span className="mb-1 block text-slate-400">Started at</span>
          <input
            type="datetime-local"
            value={startsLocal}
            min={minStartsAtLocal()}
            onChange={(e) => setStartsLocal(e.target.value)}
            className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-2 text-sm text-slate-100"
          />
        </label>

        <div className="mb-3">
          <div className="mb-1 text-xs text-slate-400">Duration</div>
          <div className="flex flex-wrap gap-1.5">
            {DURATION_PRESETS.map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => setDurationHours(h)}
                className={cn(
                  'rounded border px-2.5 py-1 text-xs',
                  durationHours === h
                    ? 'border-indigo-500 bg-indigo-600/30 text-indigo-100'
                    : 'border-slate-700 bg-slate-950 text-slate-400',
                )}
              >
                {h}h
              </button>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
            <button
              type="button"
              onClick={() => setDurationHours((d) => Math.max(12, d - 12))}
              className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-slate-200"
            >
              −12h
            </button>
            <span className="tabular-nums text-slate-200">
              {durationHours}h
            </span>
            <button
              type="button"
              onClick={() => setDurationHours((d) => Math.min(336, d + 12))}
              className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-slate-200"
            >
              +12h
            </button>
          </div>
        </div>

        <label className="mb-3 block text-xs">
          <span className="mb-1 block text-slate-400">Notes (optional)</span>
          <input
            type="text"
            maxLength={120}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-2 text-sm text-slate-100"
          />
        </label>

        {error && <div className="mb-2 text-xs text-rose-300">{error}</div>}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded border border-slate-700 bg-slate-950 py-2 text-sm text-slate-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              if (mode === 'create') create.mutate();
              else update.mutate();
            }}
            disabled={busy || !canSubmit}
            className="flex-1 rounded bg-indigo-600 py-2 text-sm font-medium hover:bg-indigo-500 disabled:opacity-50"
          >
            {busy ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
